/**
 * Format: Variant
 * -----------------------------------------------------------------------------
 * Formats Shopify storefront API variants response.
 *
 */
import { formatMoneyV2 } from '~/format/format-price'
import { decodeApiId, transformEdges } from '~/helpers/graphql'
import { getDisabledColorCodes } from '~/helpers/product'

/**
 * Format product variants array.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/ProductVariant
 * @param {Object} product - Product response.
 * @returns {Array}
 */
export function formatVariants(product) {
  const disabledColorCodes = getDisabledColorCodes(product.tags)

  return transformEdges(product.variants)
    .map(variant => formatVariant(variant, disabledColorCodes))
    .filter(variant => variant !== null)
}

/**
 * Format product variant.
 * - https://shopify.dev/docs/api/storefront/2023-07/objects/ProductVariant
 * @param {Object} variant - Variant response.
 * @param {Array} disabledColorCodes - Disabled color codes.
 * @returns {Object|null}
 */
function formatVariant(variant, disabledColorCodes) {
  const colorOption = variant?.selectedOptions?.find(option => option.name.toLowerCase() === 'color')
  const colorCode = colorOption ? colorOption.value.split('-')[0].trim() : false
  const isDisabled = colorCode ? disabledColorCodes.includes(colorCode) : false

  if (isDisabled) {
    return null
  }

  return {
    available: variant.available,
    compareAtPrice: formatMoneyV2(variant.compareAtPrice),
    id: decodeApiId(variant.id),
    image: decodeApiId(variant.image?.id) ?? false,
    imageSrc: variant.image?.src ?? false,
    inventory: variant.quantityAvailable,
    price: formatMoneyV2(variant.price),
    selectedOptions: variant.selectedOptions.map((option) => {
      return {
        name: option.name,
        value: option.value,
      }
    }),
    swatchUrl: buildSwatchUrl(variant),
    title: variant.title,
  }
}

/**
 * Format swatch URL.
 * @param {*} variant - Variant response
 * @returns {String}
 */
export function buildSwatchUrl(variant) {
  const colorOption = variant.selectedOptions.find((option) => option.name === 'Color')

  if (!colorOption) {
    return false
  }

  const colorCode = colorOption.value.split('-')[0].trim()
  const shortSku = variant.sku.split('-')[0].trim()

  return `${cnvs.product.awsBaseUrl}${shortSku}_${colorCode}.jpg`
}
